import { useCallback, useMemo } from 'react';

import {
  ConsumerBackerActivityResponse,
  useShowConsumerBackerActivity,
  useShowConsumerBackerIntent,
  useUpdateBackerIntent,
} from '@givebacks/api/backer';
import { useListUserCauseSelections } from '@givebacks/api/core';
import { useFormyValidator } from '@givebacks/formy';
import { getErrorMessage_v2 } from 'core/Utils';
import { useNavigate } from 'react-router-dom';
import { ButtonMantine, Card, Modal, Stack, Text, notifications } from 'ui';

import { OnboardingModal } from '../OnboardingModal';
import { SupportIntent } from './SupportIntent';
import { YourActivityIntent } from './YourActivityIntent';
import { OnboardingIntentForms } from './types';

const OnboardingModalFooter = () => {
  const navigate = useNavigate();
  const { mutate: updateBackerIntent, isPending: isSubmitting } = useUpdateBackerIntent();
  const { data: userCauseSelections } = useListUserCauseSelections();

  const { useGetForm } = useFormyValidator<OnboardingIntentForms>();

  const supportForm = useGetForm('supportForm');
  const yourActivityForm = useGetForm('yourActivityForm');

  const hasSelections = useMemo(() => {
    if (!supportForm) return false;
    return (
      Object.values(supportForm.values).some(Boolean) ||
      (yourActivityForm?.values && Object.values(yourActivityForm.values).some(Boolean))
    );
  }, [supportForm, yourActivityForm]);

  const onContinue = useCallback(() => {
    if (userCauseSelections && userCauseSelections.length > 0) {
      navigate('/onboarding/causes-following');
    } else {
      navigate('/onboarding/causes');
    }
  }, [userCauseSelections, navigate]);

  const handleSubmit = useCallback(() => {
    const requestIntent = supportForm?.values;
    if (yourActivityForm?.values) {
      Object.assign(requestIntent, yourActivityForm.values);
    }
    updateBackerIntent(
      {
        intent: requestIntent,
      },
      {
        onSuccess: () => {
          onContinue();
        },
        onError: (error) => {
          notifications.show({
            message: getErrorMessage_v2(error),
            variant: 'error',
          });
        },
      }
    );
  }, [updateBackerIntent, supportForm, yourActivityForm, onContinue]);

  const handleSkip = useCallback(() => {
    onContinue();
  }, [onContinue]);

  return (
    <Modal.AffixedFooter>
      <Stack gap="md" mt="lg">
        {hasSelections ? (
          <ButtonMantine size="lg" onClick={handleSubmit} loading={isSubmitting}>
            Continue
          </ButtonMantine>
        ) : (
          <ButtonMantine size="lg" variant="gray" c="gray.6" onClick={handleSkip}>
            Skip for now
          </ButtonMantine>
        )}
      </Stack>
    </Modal.AffixedFooter>
  );
};

const getHasAvailableActivityOptions = (userActivity?: ConsumerBackerActivityResponse): boolean => {
  if (userActivity && userActivity.activity) {
    // Check if user has any relevant activity in the last 60 days
    return Object.values(userActivity.activity).some(Boolean);
  }
  return false;
};

const OnboardingIntentContent = () => {
  const { isLoading: isLoadingUserIntent } = useShowConsumerBackerIntent();
  const { data: hasRecentActivity, isFetching: isFetchingUserActivity } =
    useShowConsumerBackerActivity({ select: getHasAvailableActivityOptions });

  if (isFetchingUserActivity || isLoadingUserIntent) {
    return (
      <Card w="100%" p="sm">
        <Text>Loading...</Text>
      </Card>
    );
  }

  return (
    <Card w="100%" p="sm">
      {hasRecentActivity && <YourActivityIntent />}
      <SupportIntent showActivitySection={Boolean(hasRecentActivity)} />
    </Card>
  );
};

const OnboardingIntentModal = () => {
  return (
    <OnboardingModal
      title="What brings you here today?"
      description="Select everything you'd like to do — we'll tailor your experience around it."
    >
      <OnboardingIntentContent />
      <OnboardingModalFooter />
    </OnboardingModal>
  );
};

export { OnboardingIntentModal };
